import { useNavigate, useLocation, Link } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";

// Context-aware back navigation. Maps the current path to a logical parent + label.
// Prefers in-app history (preserves filters/scroll) but falls back to the parent path
// on a fresh/direct load so Back is always reliable.
function parentFor(pathname) {
  const p = pathname.replace(/\/$/, "");
  const seg = p.split("/").filter(Boolean);
  const rules = [
    [/^\/maintenance\/jobs\/[^/]+$/, "/maintenance", "Maintenance"],
    [/^\/maintenance\/history\/[^/]+$/, "/maintenance/history", "Service History"],
    [/^\/maintenance\/(parts|history)$/, "/maintenance", "Maintenance"],
    [/^\/assets\/[^/]+\/check$/, () => `/assets/${seg[1]}`, "Asset"],
    [/^\/assets\/[^/]+$/, "/assets", "Assets"],
    [/^\/faults\/report$/, "/faults", "Faults"],
    [/^\/compliance\/roles$/, "/compliance", "Compliance"],
  ];
  for (const [re, to, label] of rules) {
    if (re.test(p)) return { to: typeof to === "function" ? to() : to, label };
  }
  return null;
}

const ROOT_LABELS = {
  "": "Home", dashboard: "Dashboard", assets: "Assets", faults: "Faults", maintenance: "Maintenance",
  "quick-data": "Quick Data", havs: "HAVS", checks: "Checks", documents: "Docs", rams: "RAMS",
  training: "Training", ppe: "PPE", compliance: "Compliance", reports: "Reports", departments: "Departments",
  people: "People", audit: "Audit", admin: "Admin", platform: "Platform", scan: "Scan", more: "More",
};

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const seg = pathname.split("/").filter(Boolean);
  if (seg.length === 0) return null;
  const crumbs = [{ to: "/", label: "Home" }];
  let acc = "";
  seg.forEach((s, i) => {
    acc += `/${s}`;
    const isId = /[0-9a-f]{8}-/.test(s) || /^\d+$/.test(s);
    const label = isId ? "Detail" : (ROOT_LABELS[s] || s.replace(/-/g, " "));
    crumbs.push({ to: acc, label, last: i === seg.length - 1 });
  });
  return (
    <nav className="hidden md:flex items-center gap-1 text-xs text-slate-400 font-body mb-1" data-testid="breadcrumbs">
      {crumbs.map((c, i) => (
        <span key={c.to} className="flex items-center gap-1">
          {i > 0 && <span>/</span>}
          {c.last ? <span className="text-slate-600 font-semibold capitalize">{c.label}</span>
            : <Link to={c.to} className="hover:text-blue-600 capitalize">{c.label}</Link>}
        </span>
      ))}
    </nav>
  );
}

export default function BackBar() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "/login") return null;
  const parent = parentFor(pathname);
  const go = () => {
    if (window.history.length > 2) nav(-1);
    else nav(parent?.to || "/");
  };
  const label = parent ? `Back to ${parent.label}` : "Back";
  return (
    <div className="mb-3">
      <button data-testid="back-button" onClick={go}
        className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors duration-100">
        <CaretLeft size={16} weight="bold" /> {label}
      </button>
      <Breadcrumbs />
    </div>
  );
}
